import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  View, Text, Modal, TouchableOpacity, StyleSheet,
  ActivityIndicator, Animated
} from 'react-native'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import Ionicons from '@expo/vector-icons/Ionicons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { supabase } from '../lib/supabase'
import { useThemeColors } from '../lib/useThemeColors'
import { fonts } from '../lib/theme'

const MAX_MISTAKES = 4
const DIFF_COLORS = { 1: '#F4D35E', 2: '#7FB069', 3: '#5DA9E9', 4: '#B185DB' }
// 1=giallo(facile) 2=verde(medio) 3=blu(tricky) 4=viola(difficile) — stessa
// logica cromatica del NYT Connections, per farlo riconoscere a chi lo conosce.

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default function ConnectionsModal({ visible, onClose }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()

  const [groups, setGroups] = useState([])       // 4 gruppi originali {group_id, category, difficulty, items}
  const [cells, setCells] = useState([])          // 16 celle {text, group_id, solved}
  const [selected, setSelected] = useState([])    // testi selezionati (max 4)
  const [solvedGroups, setSolvedGroups] = useState([]) // gruppi già risolti, in ordine
  const [mistakes, setMistakes] = useState(0)
  const [loading, setLoading] = useState(true)
  const [done, setDone] = useState(false)
  const [won, setWon] = useState(false)
  const [shakeAnim] = useState(new Animated.Value(0))

  const lang = i18n.language?.substring(0, 2) ?? 'it'
  const supportedLang = ['it', 'en', 'es'].includes(lang) ? lang : 'it'

  const loadPuzzle = useCallback(async () => {
    setLoading(true)
    setSelected([])
    setSolvedGroups([])
    setMistakes(0)
    setDone(false)
    setWon(false)
    try {
      const { data } = await supabase.rpc('fn_get_connections_puzzle', { p_lang: supportedLang })
      if (data?.length === 4) {
        setGroups(data)
        // key univoca per cella: indice progressivo, non il testo — così
        // due elementi con lo stesso testo (anche se oggi non dovrebbe più
        // capitare, i dati sono stati ripuliti) non si confondono mai tra
        // loro nella selezione/validazione.
        const flat = data.flatMap(g => (g.items ?? []).map(text => ({ text, group_id: g.group_id })))
        setCells(shuffle(flat).map((c, i) => ({ ...c, key: `${i}-${c.text}` })))
      }
    } catch (e) {
      console.error('[ConnectionsModal]', e)
    } finally {
      setLoading(false)
    }
  }, [supportedLang])

  useEffect(() => {
    if (visible) loadPuzzle()
  }, [visible, loadPuzzle])

  const triggerShake = useCallback(() => {
    Animated.sequence([
      Animated.timing(shakeAnim, { toValue: 8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: -8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 6, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start()
  }, [shakeAnim])

  const toggleCell = useCallback((key) => {
    if (done) return
    setSelected(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key)
      if (prev.length >= 4) return prev
      return [...prev, key]
    })
  }, [done])

  const remainingCells = useMemo(
    () => cells.filter(c => !solvedGroups.some(g => g.group_id === c.group_id)),
    [cells, solvedGroups]
  )

  const handleSubmit = useCallback(() => {
    if (selected.length !== 4) return
    const groupIds = selected.map(key => cells.find(c => c.key === key)?.group_id)
    const allSame = groupIds.every(id => id === groupIds[0])

    if (allSame) {
      const g = groups.find(g => g.group_id === groupIds[0])
      const newSolved = [...solvedGroups, g]
      setSolvedGroups(newSolved)
      setSelected([])
      if (newSolved.length === 4) {
        setDone(true)
        setWon(true)
      }
    } else {
      triggerShake()
      const next = mistakes + 1
      setMistakes(next)
      setSelected([])
      if (next >= MAX_MISTAKES) {
        setDone(true)
        setWon(false)
      }
    }
  }, [selected, cells, groups, solvedGroups, mistakes, triggerShake])

  const handleClose = useCallback(() => {
    onClose()
    setTimeout(() => {
      setGroups([])
      setCells([])
      setSelected([])
      setSolvedGroups([])
      setMistakes(0)
      setDone(false)
      setWon(false)
    }, 400)
  }, [onClose])

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={handleClose}>
      <View style={[styles.overlay, { paddingBottom: insets.bottom }]}>
        <View style={[styles.sheet, { backgroundColor: tc.bgCard }]}>

          <View style={[styles.header, { borderBottomColor: tc.border }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <MaterialCommunityIcons name="link-variant" size={18} color={tc.rosaText} />
              <Text style={[styles.title, { color: tc.text }]}>{t('connections.title', 'Collegamenti')}</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={[styles.closeBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={tc.rosaText} size="large" />
              <Text style={[styles.loadingText, { color: tc.textMuted }]}>{t('connections.loading', 'Preparo il puzzle...')}</Text>
            </View>
          ) : groups.length !== 4 ? (
            <View style={styles.loadingWrap}>
              <MaterialCommunityIcons name="alert-circle-outline" size={36} color={tc.textMuted} />
              <Text style={[styles.loadingText, { color: tc.textMuted }]}>{t('connections.no_puzzle', 'Puzzle non disponibile, riprova.')}</Text>
            </View>
          ) : (
            <View style={styles.content}>
              <Text style={[styles.instructions, { color: tc.textMuted }]}>
                {t('connections.instructions', 'Trova i 4 gruppi da 4 elementi ciascuno')}
              </Text>

              {/* Gruppi già risolti, in cima, colorati per difficoltà */}
              {solvedGroups.map(g => (
                <View key={g.group_id} style={[styles.solvedBanner, { backgroundColor: DIFF_COLORS[g.difficulty] }]}>
                  <Text style={styles.solvedCategory}>{g.category.toUpperCase()}</Text>
                  <Text style={styles.solvedItems}>{g.items.join(' · ')}</Text>
                </View>
              ))}

              {/* Griglia celle rimanenti */}
              <Animated.View style={[styles.grid, { transform: [{ translateX: shakeAnim }] }]}>
                {remainingCells.map((c) => {
                  const isSelected = selected.includes(c.key)
                  return (
                    <TouchableOpacity
                      key={c.key}
                      style={[
                        styles.cell,
                        { backgroundColor: isSelected ? tc.rosa : tc.bgCard2, borderColor: isSelected ? tc.rosa : tc.border },
                      ]}
                      onPress={() => toggleCell(c.key)}
                      disabled={done}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.cellText, { color: isSelected ? tc.brandText : tc.text }]} numberOfLines={3}>
                        {c.text}
                      </Text>
                    </TouchableOpacity>
                  )
                })}
              </Animated.View>

              {/* Errori rimasti */}
              {!done && (
                <View style={styles.mistakesRow}>
                  <Text style={[styles.mistakesLabel, { color: tc.textDim }]}>{t('connections.mistakes', 'Errori')}:</Text>
                  {Array.from({ length: MAX_MISTAKES }).map((_, i) => (
                    <View
                      key={i}
                      style={[styles.mistakeDot, { backgroundColor: i < mistakes ? tc.rosa : tc.border }]}
                    />
                  ))}
                </View>
              )}

              {/* Bottone conferma */}
              {!done && (
                <TouchableOpacity
                  style={[
                    styles.submitBtn,
                    { backgroundColor: selected.length === 4 ? tc.rosa : tc.bgCard2, borderColor: tc.border },
                  ]}
                  onPress={handleSubmit}
                  disabled={selected.length !== 4}
                >
                  <Text style={[styles.submitBtnText, { color: selected.length === 4 ? tc.brandText : tc.textMuted }]}>
                    {t('connections.submit', 'Conferma gruppo')}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Esito finale */}
              {done && (
                <View style={styles.resultWrap}>
                  <MaterialCommunityIcons
                    name={won ? 'trophy' : 'emoticon-neutral-outline'}
                    size={44}
                    color={won ? '#f59e0b' : tc.rosaText}
                  />
                  <Text style={[styles.resultMsg, { color: tc.text }]}>
                    {won
                      ? t('connections.won', 'Puzzle risolto!')
                      : t('connections.lost', 'Puzzle terminato — riprova con uno nuovo')}
                  </Text>
                  <TouchableOpacity style={[styles.restartBtn, { backgroundColor: tc.rosa }]} onPress={loadPuzzle}>
                    <Text style={[styles.restartBtnText, { color: tc.brandText }]}>{t('connections.new_puzzle', 'Nuovo puzzle')}</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet:        { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '92%' },
  header:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1 },
  title:        { fontFamily: fonts.condensedBold, fontSize: 16, textTransform: 'uppercase' },
  closeBtn:     { width: 32, height: 32, borderRadius: 8, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  loadingWrap:  { padding: 48, alignItems: 'center', gap: 12 },
  loadingText:  { fontSize: 13, textAlign: 'center' },
  content:      { padding: 16, paddingBottom: 32 },
  instructions: { fontSize: 13, textAlign: 'center', marginBottom: 14 },
  solvedBanner: { borderRadius: 10, padding: 10, marginBottom: 8, alignItems: 'center' },
  solvedCategory: { fontFamily: fonts.condensedBold, fontSize: 12, color: '#111', marginBottom: 2 },
  solvedItems:  { fontSize: 12, color: '#111', textAlign: 'center' },
  grid:         { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4, marginBottom: 16 },
  cell:         { width: '23.5%', aspectRatio: 1, borderRadius: 10, borderWidth: 1, alignItems: 'center', justifyContent: 'center', padding: 4 },
  cellText:     { fontSize: 11, fontWeight: '600', textAlign: 'center' },
  mistakesRow:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 16 },
  mistakesLabel:{ fontSize: 12, marginRight: 4 },
  mistakeDot:   { width: 10, height: 10, borderRadius: 5 },
  submitBtn:    { borderRadius: 12, borderWidth: 1, padding: 14, alignItems: 'center' },
  submitBtnText:{ fontFamily: fonts.condensedBold, fontSize: 14 },
  resultWrap:   { alignItems: 'center', gap: 12, paddingVertical: 16 },
  resultMsg:    { fontSize: 15, fontWeight: '600', textAlign: 'center' },
  restartBtn:   { borderRadius: 12, paddingHorizontal: 28, paddingVertical: 12, marginTop: 4 },
  restartBtnText: { fontFamily: fonts.condensedBold, fontSize: 14 },
})